import {
  withValidation,
  assert,
  reportError,
  composeSDKFactories,
  registerCorvidEvent,
  isValidMediaItemUri,
  mediaDataType,
  createElementPropsSDKFactory,
  toJSONBase,
  getMediaDataFromSrc,
  createMediaItemUri,
  isValidMediaSrc,
  getVideoDataByVideoId,
} from '@wix/editor-elements-corvid-utils';
import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';
import { getIdFromUrl } from '@wix/background-media-utils';
import {
  IVideoPlayerProps,
  IVideoPlayerSDKFactory,
  VideoPlayerOnStateUpdatedComponentEvent,
} from '../VideoPlayer.types';

enum MediaType {
  WIX_VIDEO = 'video',
  EXTERNAL_LINK = 'image',
}

const _videoPlayerSDKFactory: IVideoPlayerSDKFactory = api => {
  const { setProps, props, createSdkState, compRef, metaData, registerEvent } =
    api;
  const [state, setState] = createSdkState({
    src: '',
    currentTime: 0,
    volume: props.volume || 0,
    duration: props.duration,
    isMuted: Boolean(props.isMuted),
    isPlaying: Boolean(props.isPlaying),
    isInitialized: false,
  });

  const updateVideoSource = (
    nextSrc: string,
    updatePropsFn: (props: Partial<IVideoPlayerProps>) => void,
  ) => {
    if (nextSrc !== props.src) {
      if (state.isInitialized) {
        // set compRef's player to undefined until the VideoPlayer is reinitialized with new src
        compRef.resetPlayerRef();
      }
      setState({
        duration: undefined,
        isMuted: Boolean(props.isMuted),
        isPlaying: Boolean(props.isPlaying),
        isInitialized: false,
        src: nextSrc,
      });
      updatePropsFn({
        src: nextSrc,
      });
    }
  };

  const videoUrlToStaticVideoURL = (mediaItem: {
    src: string;
    width?: number;
    height?: number;
    uri?: string;
    title?: string;
  }) => {
    const { src, width, height, uri, title } = mediaItem;
    const mediaId = getIdFromUrl(src);
    const mediaItemUri = createMediaItemUri({
      mediaId,
      width,
      height,
      title,
      posterId: uri,
      type: 'video',
    });
    return mediaItemUri.item || src;
  };

  registerEvent(
    'onStateUpdated',
    (event: VideoPlayerOnStateUpdatedComponentEvent) => setState(event.state),
  );

  registerEvent('onPlayerInitialized', () => setState({ isInitialized: true }));

  return {
    get title() {
      return props.playableConfig.title || '';
    },

    set title(title: string) {
      setProps({
        playableConfig: {
          ...props.playableConfig,
          title,
        },
      });
    },

    get isMuted() {
      return Boolean(state.isMuted);
    },

    get isPlaying() {
      return Boolean(state.isPlaying);
    },

    get description() {
      return props.playableConfig.description || '';
    },

    set description(description: string) {
      setProps({
        playableConfig: {
          ...props.playableConfig,
          description,
        },
      });
    },

    get currentTime() {
      return state.currentTime || 0;
    },

    get duration() {
      return state.duration || 0;
    },

    get volume() {
      return state.volume;
    },

    set volume(volume: number) {
      compRef.setVolume(volume);
      setState({ volume });
    },

    get src() {
      let src = props.src;
      if (Array.isArray(props.src)) {
        src = props.src[0];
      }
      if (props.playableConfig.poster) {
        const { width, height, uri } = props.playableConfig.poster;
        if (!state.src) {
          return videoUrlToStaticVideoURL({ src, width, height, uri });
        }
      }
      return state.src || src;
    },

    set src(value: string) {
      const src = assert.isNil(value) ? '' : value;

      if (isValidMediaSrc(src, MediaType.WIX_VIDEO)) {
        const mediaData = getMediaDataFromSrc(value);
        if (!mediaData) {
          return;
        }
        if (mediaData.posterImageRef) {
          const { width, height, uri } = mediaData.posterImageRef;
          const title = mediaData.name;
          setState({
            src: videoUrlToStaticVideoURL({
              src,
              width,
              height,
              uri,
              title,
            }),
          });
        }
        if (mediaData.type === 'WixVideo') {
          const oldPlayableConfig = props.playableConfig;

          setProps(
            new Promise(resolve => {
              const videoId = mediaData.videoId;

              if (!videoId) {
                setState({ src: props.src });
                resolve({});
                return;
              }

              getVideoDataByVideoId(videoId)
                .then(fullMediaRefData => {
                  let propsToUpdate = { ...props };

                  const didPosterChange =
                    oldPlayableConfig.poster?.uri !==
                    props.playableConfig?.poster?.uri;

                  if (!didPosterChange) {
                    propsToUpdate.playableConfig = {
                      ...propsToUpdate.playableConfig,
                      poster: {
                        ...propsToUpdate.playableConfig.poster,
                        ...fullMediaRefData.mediaObject.posterImageRef,
                      },
                    };
                  }

                  updateVideoSource(
                    `https://video.wixstatic.com/${fullMediaRefData.mediaObject.qualities[0].url}`,
                    propsWithVideoSource => {
                      propsToUpdate = {
                        ...propsToUpdate,
                        ...propsWithVideoSource,
                      };
                    },
                  );

                  resolve(propsToUpdate);
                })
                .catch(err => {
                  resolve({});
                  throw err;
                });
            }),
          );
        }
      } else if (isValidMediaSrc(src, MediaType.EXTERNAL_LINK)) {
        // When getting a custom extenal link, reset `playableConfig`.
        setProps({
          playableConfig: { title: props.playableConfig.title },
        });
        updateVideoSource(value, setProps);
      } else {
        reportError(
          `The "src" property cannot be set to "${value}". It must be a valid URL starting with "http://", "https://", or a valid video URL starting with "wix:video://".`,
        );
      }
    },

    get poster() {
      const { width, height, uri } = props.playableConfig!.poster!;
      const { title } = props.playableConfig;

      const mediaItemUri = createMediaItemUri({
        mediaId: uri,
        width,
        height,
        title,
        type: 'image',
      });

      return mediaItemUri.item || '';
    },

    set poster(uri: string) {
      let poster: Partial<mediaDataType> | null = props.playableConfig?.poster
        ? props.playableConfig?.poster
        : {};

      if (isValidMediaItemUri(uri, 'image')) {
        poster = getMediaDataFromSrc(uri);
        uri = poster?.mediaId || uri;
      }

      setProps({
        playableConfig: {
          ...props.playableConfig,
          ...(poster ? { title: poster.title } : {}),
          poster: {
            ...poster,
            uri,
          },
        },
      });
    },

    play() {
      return compRef.play();
    },

    pause() {
      return compRef.pause();
    },

    stop() {
      return compRef.stop();
    },

    togglePlay() {
      return compRef.togglePlay();
    },

    mute() {
      setState({ isMuted: true });
      return compRef.mute();
    },

    unmute() {
      setState({ isMuted: false });
      return compRef.unmute();
    },

    seek(timePoint: number) {
      return compRef.seek(timePoint);
    },

    onPlay: handler => registerCorvidEvent('onPlay', api, handler),

    onPause: handler => registerCorvidEvent('onPause', api, handler),

    onEnded: handler => registerCorvidEvent('onEnded', api, handler),

    onProgress: handler => registerCorvidEvent('onProgress', api, handler),

    toJSON() {
      return {
        ...toJSONBase(metaData),
        title: props.playableConfig.title || '',
        isPlaying: Boolean(props.isPlaying),
        currentTime: state.currentTime || 0,
        duration: props.duration || 0,
        volume: state.volume || 0,
        isMuted: Boolean(state.isMuted),
      };
    },
  };
};

const videoPlayerSDKFactory: IVideoPlayerSDKFactory = withValidation(
  _videoPlayerSDKFactory,
  {
    type: ['object'],
    properties: {
      title: { type: ['string', 'nil'], warnIfNil: true },
      description: { type: ['string', 'nil'], warnIfNil: true },
      volume: {
        type: ['number', 'nil'],
        minimum: 0,
        maximum: 100,
        warnIfNil: true,
      },
      poster: { type: ['string', 'nil'], warnIfNil: true },
      src: {
        type: ['string', 'nil'],
        warnIfNil: true,
      },
    },
  },
);
const elementPropsSDKFactory = createElementPropsSDKFactory();

export const sdk: IVideoPlayerSDKFactory = composeSDKFactories([
  elementPropsSDKFactory,
  videoPlayerSDKFactory,
]);

export default createComponentSDKModel(sdk);
